import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, User, ArrowLeft, ArrowRight, CheckCircle2, Gift, Sparkles } from "lucide-react";
import { Seo } from "@/components/Seo";
import FeaturedSoftware from "@/components/FeaturedSoftware";
import { QuizPromo } from "@/components/QuizPromo";
import AdVideo from "@/components/AdVideo";
import { api } from "@/lib/api";
import { AppBadges } from "@/components/AppBadges";

const fade = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

// Prominent "get the app" band — only appears once Ashley has a live store link.
function AppDownloadBand() {
  const [has, setHas] = useState(false);
  useEffect(() => {
    api.get("/settings/app-links").then((r) => setHas(!!(r.data?.apple || r.data?.google))).catch(() => {});
  }, []);
  if (!has) return null;
  return (
    <div className="border-t border-white/10 bg-gradient-to-b from-violet-950/40 to-[#0F172A] px-5 sm:px-8 py-14" data-testid="landing-app-band">
      <div className="max-w-3xl mx-auto text-center">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-violet-400">Take it with you</span>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-white mt-2 mb-6">Get the Salesman Connect app</h2>
        <AppBadges align="center" />
      </div>
    </div>
  );
}

const ROLES = {
  business: {
    icon: Briefcase,
    title: "Business",
    tag: "I'm hiring",
    grad: "bg-gradient-to-bl from-emerald-900/80 via-emerald-950/70 to-slate-950",
    accent: "text-emerald-300",
    dot: "text-emerald-400",
    btn: "bg-emerald-500 hover:bg-emerald-400 text-slate-950",
    tag: "I'm Hiring",
    heading: "Welcome, industry leaders!",
    points: ["Find elite, vetted closers", "Post a role in minutes", "Hire fast, chat instantly"],
    to: "/join/business",
    key: "business",
  },
  sales: {
    icon: User,
    title: "Salesperson",
    tag: "I'm selling",
    grad: "bg-gradient-to-br from-violet-900/80 via-violet-950/70 to-slate-950",
    accent: "text-violet-300",
    dot: "text-violet-400",
    btn: "bg-violet-600 hover:bg-fuchsia-500 text-white",
    tag: "I'm Selling",
    heading: "Calling all one-call closers.",
    points: ["Built for high-ticket in-home closers", "Bath, roofing, HVAC, solar, windows & more", "Get discovered by top home-improvement companies"],
    to: "/join/salesman",
    key: "sales",
  },
};

// Warm the lazy chunk the panel will navigate into, so the signup funnel feels instant
// even with route-level code-splitting. import() is idempotent (webpack caches the chunk).
const prefetchRoute = {
  business: () => import("@/pages/HireIntro"),
  sales: () => import("@/pages/CoreIntro"),
};

function Panel({ roleKey, onSelect }) {
  const r = ROLES[roleKey];
  const Icon = r.icon;
  const warm = () => { try { prefetchRoute[roleKey]?.(); } catch (e) {} };
  return (
    <button
      onClick={() => onSelect(roleKey)}
      onMouseEnter={warm}
      onFocus={warm}
      onTouchStart={warm}
      data-testid={`role-${r.key}-select`}
      aria-label={`Choose ${r.title}`}
      className={`relative flex-1 basis-0 flex flex-col justify-center items-center px-6 py-12 md:py-16 min-h-[30vh] md:min-h-[60vh] cursor-pointer overflow-hidden group text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${r.grad}`}
    >
      <div aria-hidden className="absolute inset-0 opacity-[0.15] mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 0.5px, transparent 0.5px)", backgroundSize: "22px 22px" }} />
      <div className="relative z-10">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-none px-4">{r.title}</h2>
        <span className={`mt-8 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shadow-lg ${r.btn}`}>
          <Icon className="w-4 h-4" /> {r.tag} <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </button>
  );
}

function Pitch({ roleKey, onBack, revealed }) {
  const r = ROLES[roleKey];
  const Icon = r.icon;
  return (
    <div className={`relative w-full min-h-screen flex flex-col items-center text-center px-6 ${r.key === "sales" ? "justify-start sm:justify-center pt-16 pb-10 sm:py-6" : "justify-center py-10"} ${r.grad}`} data-testid={`pitch-${r.key}`}>
      <div aria-hidden className="absolute inset-0 opacity-[0.15] mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 0.5px, transparent 0.5px)", backgroundSize: "22px 22px" }} />
      <button onClick={onBack} data-testid="switch-role-btn"
        className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm sm:text-base font-bold bg-white/10 backdrop-blur-md border border-white/30 text-white shadow-lg hover:bg-white/20 hover:scale-[1.03] transition-all duration-200 focus:outline-none">
        <ArrowLeft className="w-5 h-5" /> Go back
      </button>
      <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }} className="relative z-10 max-w-2xl">
        {r.key === "sales" && (
          <motion.div variants={fade} className="relative inline-block mb-2">
            <div aria-hidden className="absolute inset-3 -z-10 blur-3xl opacity-40 bg-gradient-to-br from-violet-600 to-emerald-500 rounded-full" />
            <motion.img src="/connie-avatar.png" alt="Connie, your sales guide" data-testid="pitch-connie"
              animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="w-36 h-36 sm:w-44 sm:h-44 object-contain drop-shadow-2xl mx-auto" />
          </motion.div>
        )}
        <motion.span variants={fade} className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] ${r.accent} mb-4`}>
          <Icon className="w-4 h-4" /> {r.tag}
        </motion.span>
        <motion.h1 variants={fade} className="text-2xl md:text-3xl font-black tracking-tighter text-white">{r.heading}</motion.h1>
        {r.key === "sales" && (
          <motion.p variants={fade} data-testid="sales-free-note" className="mt-3 text-sm sm:text-base font-semibold text-emerald-300">
            100% always free for salespeople. No credit card needed, EVER.
          </motion.p>
        )}
        {r.key !== "sales" && (
          <motion.ul variants={fade} className="mt-6 space-y-3 text-left inline-block">
            {r.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-base md:text-lg font-medium text-white/90">
                <CheckCircle2 className={`w-5 h-5 shrink-0 ${r.dot}`} /> {p}
              </li>
            ))}
          </motion.ul>
        )}
        <motion.div variants={fade} className={`mt-8 transition-opacity duration-700 ${revealed ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          {r.key === "sales" ? (
            <>
              <img src="/core-logo.png" alt="C.O.R.E. Sales Assessment logo" className="w-12 h-12 mx-auto mb-3 object-contain drop-shadow-xl" data-testid="pitch-core-logo" />
              <p className="text-sm sm:text-base text-white/70 max-w-md mx-auto">
                Every rep starts with a quick <span className="text-white font-semibold">C.O.R.E. sales assessment</span>. It's how the right companies find you faster.
              </p>
              <Link to="/core-intro" data-testid="pitch-take-quiz">
                <span className={`mt-5 inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-base sm:text-lg font-black shadow-xl transition-colors ${r.btn}`}>
                  <Sparkles className="w-5 h-5" /> Take the assessment <ArrowRight className="w-5 h-5" />
                </span>
              </Link>
            </>
          ) : (
            <Link to="/hire-intro" data-testid={`signup-${r.key}-btn`}>
              <span className={`inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-base sm:text-lg font-black shadow-xl transition-colors ${r.btn}`}>
                Get Started <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          )}
          <p className="mt-5 text-sm text-white/60">
            Already have an account?{" "}
            <Link to="/login" data-testid="login-link" className="text-white font-semibold underline underline-offset-4 hover:text-white/80 transition-colors">Log in here</Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

function IntroVideo({ roleKey, onDone }) {
  const ref = useRef(null);
  const storeKey = `sc_intro_seen_${roleKey}`;
  const [canSkip, setCanSkip] = useState(false);
  // Always use the full-quality clip; it loads fast enough on mobile too.
  const [src] = useState("/intro-logo.mp4");

  const end = useCallback(() => {
    try { localStorage.setItem(storeKey, "1"); } catch (e) { console.warn("intro: could not persist seen flag", e); }
    onDone();
  }, [storeKey, onDone]);

  useEffect(() => {
    try { setCanSkip(localStorage.getItem(storeKey) === "1"); } catch (e) { console.warn("intro: could not read seen flag", e); }
    const v = ref.current;
    if (v) {
      v.muted = false;
      v.volume = 1;
      const p = v.play();
      if (p && p.catch) p.catch(() => { v.muted = true; v.play().catch(() => {}); }); // fall back to silent if sound is blocked
    }
    // stall-guard: if the clip never loads any data, don't trap the user on black
    const guard = setTimeout(() => { const el = ref.current; if (!el || el.readyState === 0) end(); }, 4500);
    // hard cap: never linger past 11s (then AnimatePresence fades it out)
    const maxCap = setTimeout(end, 11000);
    return () => { clearTimeout(guard); clearTimeout(maxCap); };
  }, [end, storeKey]);

  return (
    <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
      onClick={canSkip ? end : undefined}
      className={`fixed inset-0 z-[70] bg-black flex items-center justify-center ${canSkip ? "cursor-pointer" : ""}`} data-testid="intro-video">
      <video ref={ref} src={src} autoPlay playsInline preload="auto" onEnded={end} onError={end}
        className="w-full h-full object-contain" data-testid="intro-video-el" />
      {canSkip ? (
        <button onClick={(e) => { e.stopPropagation(); end(); }} data-testid="intro-skip"
          className="absolute bottom-8 right-8 text-sm text-white/70 hover:text-white transition-colors">Skip →</button>
      ) : (
        <span data-testid="intro-firsttime" className="absolute bottom-8 right-8 text-xs text-white/40 select-none">✨ Enjoy the intro</span>
      )}
    </motion.div>
  );
}

export default function LandingSplitScreen() {
  const [selected, setSelected] = useState(null);
  const [intro, setIntro] = useState(false);
  const [pitchRevealed, setPitchRevealed] = useState(false);
  const salesAudioRef = useRef(null); // Connie narration when the salesperson pitch appears

  const handleSelect = (roleKey) => {
    setSelected(roleKey); setPitchRevealed(false); setIntro(true);
    // Fire an early intent ping so the owner gets an email when someone taps I'm hiring / I'm selling.
    try { api.post("/intent", { kind: roleKey === "business" ? "hiring" : "selling" }).catch(() => {}); } catch { /* ignore */ }
  };

  // Reveal the pitch CTA only after Connie finishes talking. Business has no clip, so it
  // reveals immediately; muted users also reveal immediately.
  const introDone = () => {
    setIntro(false);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    let muted = false;
    try { muted = localStorage.getItem("connie_core_muted") === "1"; } catch { /* noop */ }
    if (selected !== "sales" || muted) { setPitchRevealed(true); return; }
    const el = salesAudioRef.current;
    if (!el) { setPitchRevealed(true); return; }
    el.currentTime = 0;
    const reveal = () => setPitchRevealed(true);
    const armFromDuration = (extra) => {
      const d = Number.isFinite(el.duration) ? el.duration : 0;
      if (d > 0) setTimeout(reveal, d * 1000 + extra);
    };
    // Keep the "Take the assessment" CTA hidden until Connie finishes talking. onEnded (on the
    // <audio> element) is the primary trigger; the timers below are only safety nets.
    const p = el.play();
    if (p && p.catch) p.catch(() => {
      // Autoplay blocked: still wait out the clip length if known, otherwise a short grace period.
      if (Number.isFinite(el.duration) && el.duration > 0) armFromDuration(600);
      else setTimeout(reveal, 6000);
    });
    // Stall safety: reveal shortly after the clip should have ended, in case onEnded never fires.
    if (Number.isFinite(el.duration) && el.duration > 0) armFromDuration(2500);
    else el.addEventListener("loadedmetadata", () => armFromDuration(2500), { once: true });
    setTimeout(reveal, 60000); // absolute hard cap
  };

  return (
    <div className="bg-[#0F172A]" data-testid="landing-split">
      <h1 className="sr-only">In-Home Sales Jobs for One-Call Closers, Hire or Get Hired</h1>
      <Seo
        title="In-Home Sales Jobs for One-Call Closers | Salesman Connect"
        description="The #1 platform for high-ticket in-home sales. Find one-call-close sales jobs in roofing, solar, HVAC & remodeling, or hire elite closers. Free to join."
        path="/"
        image="https://salesmanconnect.com/in-home-sales.jpg"
        jsonLd={{
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "What is Salesman Connect?", acceptedAnswer: { "@type": "Answer", text: "Salesman Connect is the #1 platform for one-call-close, high-ticket in-home sales. It connects home-improvement companies (bath & shower remodeling, roofing, HVAC, solar, windows and more) with elite, vetted in-home sales closers. Reps build a free profile, get discovered, and apply to in-home sales jobs near them." } },
            { "@type": "Question", name: "How do I find in-home sales jobs near me?", acceptedAnswer: { "@type": "Answer", text: "Create a free salesperson profile, browse one-call-close in-home sales roles by location and product, and apply with one click, every application opens a direct chat with the hiring company." } },
            { "@type": "Question", name: "What is one-call-close in-home sales?", acceptedAnswer: { "@type": "Answer", text: "One-call-close in-home sales is a high-ticket sales process where the rep meets a homeowner in their home and closes the deal in a single visit, common in bath remodeling, roofing, HVAC, solar and windows, where top closers earn $150k–$300k+." } },
          ],
        }}
      />
      <AnimatePresence mode="wait">
        {selected ? (
          <motion.div key="pitch" className="force-dark bg-[#0F172A]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <Pitch roleKey={selected} onBack={() => setSelected(null)} revealed={pitchRevealed} />
          </motion.div>
        ) : (
          <motion.div key="split" initial={false} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            className="flex flex-col min-h-screen overflow-hidden">
            <div className="force-dark bg-[#0F172A]">
            <div className="bg-black flex flex-nowrap items-center justify-between gap-x-3 px-4 sm:px-8 py-3 sm:py-4" data-testid="landing-topbar">
              <div className="flex items-center gap-2 min-w-0" data-testid="landing-logo">
                <img src="/logo-mark.png?v=2" alt="Salesman Connect" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                <span className="hidden md:inline text-2xl font-extrabold tracking-tighter text-white">Salesman<span className="text-emerald-400">Connect</span></span>
                <span className="hidden md:inline-block ml-1 px-2.5 py-1 rounded-md bg-violet-500/20 border border-violet-400/30 text-[11px] font-bold uppercase tracking-wider text-violet-200">For one-call closers</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Link to="/sales-types" data-testid="landing-core-link"
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-bold bg-violet-500/20 backdrop-blur-md border border-violet-400/30 text-violet-100 shadow-lg hover:bg-violet-500/30 hover:scale-[1.03] transition-all duration-200">
                  <img src="/core-logo.png" alt="C.O.R.E. sales assessment" className="w-5 h-5 object-contain" /> C.O.R.E.
                </Link>
                <Link to="/login" data-testid="landing-login-btn"
                  className="inline-flex items-center gap-1.5 sm:gap-2.5 rounded-full px-3.5 py-2 sm:px-6 sm:py-2.5 text-sm sm:text-base font-bold bg-white/10 backdrop-blur-md border border-white/25 text-white shadow-lg hover:bg-white/20 hover:scale-[1.03] transition-all duration-200">
                  <span className="hidden sm:inline">Already have an account?&nbsp;</span><span className="text-emerald-300">Log in</span> <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="flex flex-col-reverse md:flex-row flex-1">
              {/* Business, bottom on mobile, left on desktop */}
              <div className="flex flex-col md:w-1/2">
                <Link to="/join/business" data-testid="home-launch-banner"
                  className="flex items-center justify-center gap-2 px-5 h-16 shrink-0 text-center bg-amber-400/[0.08] border-b border-amber-300/20 hover:bg-amber-400/[0.14] transition-colors duration-300">
                  <Gift className="w-4 h-4 text-amber-300 shrink-0" />
                  <p className="text-sm text-amber-200 font-semibold leading-tight">
                    New businesses get <span className="text-amber-300 font-extrabold">300 free Core Credits</span>, a $297 value
                  </p>
                  <ArrowRight className="w-4 h-4 text-amber-300 shrink-0 hidden sm:inline" />
                </Link>
                <Panel roleKey="business" onSelect={handleSelect} />
              </div>
              {/* Salesperson, top on mobile, right on desktop */}
              <div className="flex flex-col md:w-1/2 md:border-l border-white/10">
                <div className="h-16 shrink-0 border-b border-white/10"><QuizPromo variant="banner" /></div>
                <Panel roleKey="sales" onSelect={handleSelect} />
              </div>
            </div>
            </div>
            <div className="border-t border-slate-200 bg-white px-5 sm:px-8 py-12" data-testid="landing-video-section">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900 mb-6">The Skills-First Way To Get Hired &amp; To Hire</h2>
                <AdVideo />
              </div>
            </div>

            <AppDownloadBand />

            <FeaturedSoftware />

            <section className="border-t border-slate-200 bg-slate-50 px-5 sm:px-8 py-16" data-testid="landing-seo-content">
              <div className="max-w-4xl mx-auto">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">The #1 platform for in-home sales</span>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-2 mb-5">High-Ticket, One-Call-Close In-Home Sales, Built for Closers &amp; the Companies That Hire Them</h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  Salesman Connect is the skills-first talent platform built <em>exclusively</em> for one-call-close, high-ticket
                  in-home sales. We connect elite, vetted in-home sales closers with home-improvement companies across bath &amp;
                  shower remodeling, roofing, solar, HVAC, windows, siding, gutters and more. No resume keyword games, no recruiter
                  fees, just proven closers matched to the companies that need them, and it's free to join on both sides.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-10">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                    <h3 className="text-xl font-bold text-emerald-700 mb-3">For salespeople, get discovered, free</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      Build a free profile, take the free C.O.R.E. sales assessment to reveal your closing style, and apply to
                      in-home sales jobs near you with one click. Every application opens a direct chat with the hiring company, no gatekeepers. Applying is always 100% free for reps.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li><Link to="/jobs" className="text-emerald-700 hover:text-emerald-800 font-semibold underline underline-offset-2">Browse in-home sales jobs</Link></li>
                      <li><Link to="/sales-jobs/near-me" className="text-emerald-700 hover:text-emerald-800 font-semibold underline underline-offset-2">Find in-home sales jobs near me</Link></li>
                      <li><Link to="/core-assessment" className="text-emerald-700 hover:text-emerald-800 font-semibold underline underline-offset-2">Take the free C.O.R.E. sales assessment</Link></li>
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-violet-200 bg-violet-50 p-6">
                    <h3 className="text-xl font-bold text-violet-700 mb-3">For companies, hire proven closers</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      Post your first role free, browse skills-first profiles of vetted one-call closers, and see how each rep
                      sells before you ever interview. Filter by product, location and experience, and unlock full candidate
                      details with Core Credits (new businesses get 300 free).
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li><Link to="/hire-sales-reps" className="text-violet-700 hover:text-violet-800 font-semibold underline underline-offset-2">Hire in-home sales reps</Link></li>
                      <li><Link to="/talent" className="text-violet-700 hover:text-violet-800 font-semibold underline underline-offset-2">Browse sales talent</Link></li>
                      <li><Link to="/employers" className="text-violet-700 hover:text-violet-800 font-semibold underline underline-offset-2">For employers</Link></li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-slate-900 mt-12 mb-3">In-home sales jobs by industry</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  We cover every high-ticket home-improvement vertical, with dedicated hubs listing every city currently hiring:
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    ["Roofing", "roofing"], ["Solar", "solar"], ["HVAC", "hvac"], ["Windows & Doors", "windows"],
                    ["Bath Remodeling", "bathroom-remodeling"], ["Siding", "siding"], ["Gutters", "gutters"], ["Gutter Guards", "gutter-guards"],
                  ].map(([label, slug]) => (
                    <Link key={slug} to={`/sales-jobs/${slug}`}
                      className="inline-flex items-center rounded-full px-4 py-1.5 text-xs font-bold bg-slate-100 border border-slate-200 text-slate-700 hover:bg-violet-100 hover:border-violet-300 hover:text-violet-800 transition-colors">
                      {label} sales jobs
                    </Link>
                  ))}
                </div>

                <h3 className="text-xl font-bold text-slate-900 mt-12 mb-3">What is one-call-close, high-ticket in-home sales?</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  One-call-close in-home sales is a high-ticket sales process where a rep meets a homeowner in their home and closes
                  the deal in a single visit. It's the standard in bath remodeling, roofing, HVAC, solar and windows, industries
                  where deals run into the tens of thousands and buyers expect a confident, consultative expert at the kitchen
                  table. It's a different craft from car sales, real estate, insurance or B2B/SaaS, and it's the only thing
                  Salesman Connect is built for.
                </p>

                <h3 className="text-xl font-bold text-slate-900 mt-10 mb-3">How much do in-home sales reps earn?</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Top one-call closers routinely earn <strong className="text-slate-900">$150k–$300k+</strong> per year, commission-based,
                  no degree required. Earnings vary by company, product and effort and are never guaranteed, but few careers reward
                  raw selling skill as directly as high-ticket in-home sales. Not sure if you're wired for it?{" "}
                  <Link to="/core-assessment" className="text-emerald-700 hover:text-emerald-800 font-semibold underline underline-offset-2">Take the free C.O.R.E. assessment</Link> and find out in five minutes.
                </p>

                <h3 className="text-xl font-bold text-slate-900 mt-10 mb-4">How Salesman Connect works</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-bold text-emerald-700 mb-2">If you sell:</p>
                    <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-600">
                      <li>Create a free profile and take the C.O.R.E. sales assessment to reveal your closing style.</li>
                      <li>Get matched to one-call-close in-home sales jobs near you, by product and location.</li>
                      <li>Apply in one click and chat directly with the hiring company, no gatekeepers, no fees.</li>
                    </ol>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-violet-700 mb-2">If you hire:</p>
                    <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-600">
                      <li>Post your role free and describe the exact closer you're looking for.</li>
                      <li>Browse skills-first profiles and see how each rep actually sells before you interview.</li>
                      <li>Unlock full candidate details with Core Credits and start the conversation instantly.</li>
                    </ol>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mt-6">
                  Whether you're a seasoned closer chasing a bigger comp plan or a home-improvement company tired of sifting through
                  unqualified resumes, Salesman Connect removes the guesswork from high-ticket in-home sales hiring. Reps are ranked on
                  real, demonstrated selling ability, not keyword-stuffed resumes, so companies hire with confidence and closers get
                  discovered for exactly what makes them great. We focus on one thing and do it better than any general job board:
                  matching elite one-call closers with the bath, roofing, solar, HVAC and windows companies that need them. It's free
                  to create a profile, free to apply, and free to post your first role. Join today and see why the best one-call closers, and the fastest-growing home-services brands in the country, are making Salesman Connect their home for in-home sales.
                </p>
                <p className="text-slate-600 text-sm leading-relaxed mt-4">
                  From Texas and Florida to California, Arizona and the Carolinas, thousands of homeowners buy new roofs, solar systems,
                  bath remodels, windows and HVAC upgrades every single day, and every one of those sales starts with a skilled in-home
                  closer at the kitchen table. Salesman Connect connects that demand with proven talent nationwide, so ambitious reps
                  find high-paying in-home sales jobs near them and growing home-improvement companies build closing teams that actually
                  hit quota. Search sales jobs by city, product and pay, or post an open role and start hiring today, free.
                </p>
              </div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {intro && <IntroVideo roleKey={selected} onDone={introDone} />}
      </AnimatePresence>
      <audio ref={salesAudioRef} src="/connie-core/landing-sales.mp3" preload="auto" data-testid="landing-sales-audio"
        onEnded={() => setPitchRevealed(true)} onError={() => setTimeout(() => setPitchRevealed(true), 10000)} />
    </div>
  );
}
